import React, { useState } from 'react';
import { Drawer, Button, Modal, Spin } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import PreviewDetail from '../PreviewDetail';
import s from './s.module.less';
import useGetCaseDetail from 'hooks/textVisit/useGetCaseDetail';
import { TextVisitServiceType } from 'types/textVisit';
import { getRefillServiceTitle } from 'pages/dashboard/TextVisitPage/utils';

interface PreviewDrawerProps {
    show: boolean;
    onClose: () => void;
    onAccept: (caseId: number) => void;
    textVisitUuid?: string;
    acceptLoading?: boolean;
    isProviderEligible?: boolean;
    shouldMaskData?: boolean; // If true, mask sensitive data (for all cases tab)
}

const PreviewDrawer: React.FC<PreviewDrawerProps> = ({
    show,
    onClose,
    onAccept,
    textVisitUuid,
    acceptLoading,
    isProviderEligible,
    shouldMaskData = false,
}) => {
    const [caseData, loading, fetchData] = useGetCaseDetail({ caseUuid: textVisitUuid });

    const isRefill =
        caseData?.serviceType === TextVisitServiceType.MEDICATION_REFILL ||
        caseData?.serviceType === TextVisitServiceType.PRESCRIPTION_REFILL;

    const caseTitle =
        caseData && isRefill
            ? getRefillServiceTitle({
                  serviceType: caseData.serviceType,
                  specialty: (caseData as any).specialty ?? null,
                  assessmentName: caseData.assessmentName ?? null,
              }) || ''
            : caseData?.assessmentName || '';

    return (
        <Drawer
            open={show}
            width={1168}
            onClose={onClose}
            placement="right"
            rootClassName={s.drawer}
            title={
                <div className={s.header}>
                    <span className={s.headerTitle}>Text visit: {caseTitle}</span>
                    <div className="flex items-center gap-2">
                        {caseData?.payoutAmount && (
                            <div className={s.priceTag}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                >
                                    <path
                                        d="M6.43338 5.41784C6.58818 5.31464 6.77939 5.2224 7 5.15101L7.00001 6.84899C6.77939 6.7776 6.58818 6.68536 6.43338 6.58216C6.06927 6.33942 6 6.1139 6 6C6 5.8861 6.06927 5.66058 6.43338 5.41784Z"
                                        fill="#10B981"
                                    />
                                    <path
                                        d="M9.00002 10.849L9.00001 9.15101C9.22062 9.2224 9.41184 9.31464 9.56663 9.41784C9.93075 9.66058 10 9.8861 10 10C10 10.1139 9.93075 10.3394 9.56664 10.5822C9.41184 10.6854 9.22063 10.7776 9.00002 10.849Z"
                                        fill="#10B981"
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM9 3C9 2.44772 8.55228 2 8 2C7.44772 2 7 2.44772 7 3V3.09199C6.3784 3.20873 5.80348 3.43407 5.32398 3.75374C4.6023 4.23485 4 5.00933 4 6C4 6.99067 4.6023 7.76515 5.32398 8.24626C5.80348 8.56593 6.37841 8.79128 7.00001 8.90801L7.00002 10.8492C6.60902 10.7223 6.31917 10.5319 6.15667 10.3446C5.79471 9.92748 5.16313 9.88274 4.74599 10.2447C4.32885 10.6067 4.28411 11.2382 4.64607 11.6554C5.20855 12.3036 6.05956 12.7308 7 12.9076L7 13C6.99999 13.5523 7.44769 14 7.99998 14C8.55226 14 8.99999 13.5523 9 13L9 12.908C9.62161 12.7913 10.1965 12.5659 10.676 12.2463C11.3977 11.7651 12 10.9907 12 10C12 9.00933 11.3977 8.23485 10.676 7.75373C10.1965 7.43407 9.6216 7.20873 9.00001 7.09199L9 5.15075C9.391 5.27771 9.68085 5.4681 9.84335 5.65538C10.2053 6.07252 10.8369 6.11726 11.254 5.7553C11.6712 5.39335 11.7159 4.76176 11.354 4.34462C10.7915 3.69637 9.94046 3.26915 9 3.09236V3Z"
                                        fill="#10B981"
                                    />
                                </svg>
                                {caseData.payoutAmount}
                            </div>
                        )}
                        <Button type="text" icon={<CloseOutlined />} onClick={onClose} className={s.closeBtn} />
                    </div>
                </div>
            }
            closeIcon={null}
            footer={
                !loading && caseData ? (
                    <div className={s.footer}>
                        <Button type="primary" onClick={() => onAccept(caseData.id)} loading={acceptLoading}>
                            Accept case
                        </Button>
                        <Button onClick={onClose}>Cancel</Button>
                    </div>
                ) : null
            }
            destroyOnClose
        >
            <div className={s.loadingWrapper}>
                <Spin spinning={loading} tip="Loading">
                    {caseData ? (
                        <div className={s.content}>
                            <PreviewDetail
                                caseData={caseData}
                                isProviderEligible={isProviderEligible}
                                shouldMaskData={shouldMaskData}
                            />
                        </div>
                    ) : (
                        <div style={{ minHeight: 'calc(100vh - 208px)' }} />
                    )}
                </Spin>
            </div>
        </Drawer>
    );
};

export default PreviewDrawer;
